import { useTranslation } from 'react-i18next'

import { Alert, Box, Text } from '@enechain/ecloud-designsystem'

import { AlertDismissButton } from '~/components/common/AlertDismissButton'
import type { BidCsvImportNotice } from '~/models/bidCsvImportReturn'

type Props = {
  notice: BidCsvImportNotice
  /** 閉じる (×)。 無ければ × を出さない (取込画面に残る 0 件のとき) */
  onDismiss?: () => void
} & Omit<
  React.ComponentProps<typeof Alert>,
  'children' | 'description' | 'status' | 'title'
>

/**
 * 時間前入札 CSV の確定で対象外として除いた行があった時のエラーバナー
 * (AC-BCI-038・AC-BCI-039。 `ui-message-guide.md`「部品の選び方」 の一部だけの失敗)。
 * 取り込んだ件数 (0 件の時は出さない) と、 取り込まなかった件数を理由ごとに並べる。
 * 全部取り込めた時は成功のトーストにし、 このバナーは出さない。
 */
export const BidCsvImportExcludedBanner = ({
  notice,
  onDismiss,
  ...alertProps
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { excluded } = notice
  const lines: string[] = []
  if (notice.importedCount > 0) {
    lines.push(
      t('page:biddingCommon.csvImportDone.title', {
        count: notice.importedCount,
      }),
    )
  }
  if (excluded.gcClosed > 0) {
    lines.push(
      t('page:biddingCommon.csvImportDone.excludedGcClosed', {
        count: excluded.gcClosed,
      }),
    )
  }
  if (excluded.issuerUnregistered > 0) {
    lines.push(
      t('page:biddingCommon.csvImportDone.excludedIssuerUnregistered', {
        count: excluded.issuerUnregistered,
      }),
    )
  }

  return (
    <Alert flexShrink={0} {...alertProps} status="error">
      <Box flex="1" minWidth={0} paddingRight={2}>
        {lines.map((line, index) =>
          index === 0 ? (
            <Text key={line} fontWeight="semibold">
              {line}
            </Text>
          ) : (
            <Text key={line} marginTop={1} fontSize="sm">
              {line}
            </Text>
          ),
        )}
      </Box>
      {onDismiss !== undefined && <AlertDismissButton onClick={onDismiss} />}
    </Alert>
  )
}
